import React, { useMemo } from 'react';

import LoadingIndicator from '@/features/ai-chat/components/AskGXDrawer/LoadingIndicator/LoadingIndicator';
import LoadingText from '@/features/ai-chat/components/AskGXDrawer/LoadingText/LoadingText';
import RateAnswer from '@/features/ai-chat/components/AskGXDrawer/Messages/RateAnswer/RateAnswer';
import type { AIMessage, CatalogRow } from '@/features/ai-chat/types/chat.types';
import { ContentCopyOutlined as ContentCopyOutlinedIcon } from '@/shared/ui/icons';
import { Box } from '@/shared/ui/layout/Box';
import { ButtonBase } from '@/shared/ui/primitives/ButtonBase';
import Typography from '@/shared/ui/typography/Text';
import copyToClipboard from '@/utils/copyToClipboard';

const MessageFooter = ({
  msg,
  isLastMessage,
  isSending,
  version,
  prompt,
  conversationId,
  onRate
}: {
  msg: AIMessage;
  isLastMessage: boolean;
  isSending: boolean;
  version: number;
  prompt: string;
  conversationId: string | null;
  onRate: (id: string, score: -1 | 1) => void;
}) => {
  const response = useMemo<Array<string | CatalogRow[]>>(() => {
    return msg.content.reduce<Array<string | CatalogRow[]>>((acc, part) => {
      switch (part.kind) {
        case 'text':
          acc.push(part.text);
          break;
        case 'tool_result':
          if (part.display === 'table') {
            acc.push(part.rows);
          }
          break;
        default:
          break;
      }
      return acc;
    }, []);
  }, [msg.content]);

  const textResponse = useMemo(
    () =>
      msg.content
        .filter(part => part.kind === 'text')
        .map(part => part.text)
        .join(''),
    [msg.content]
  );

  return (
    <Box
      sx={{
        mt: 1,
        display: isLastMessage && !isSending ? 'flex' : undefined,
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      {isLastMessage && isSending && <LoadingText firstChunkArrived={msg.content.length > 0} />}
      {isLastMessage && <LoadingIndicator animate={isSending} />}
      {((isLastMessage && !isSending) || !isLastMessage) && (
        <Box
          sx={{
            color: 'gx.textDisabled',
            justifySelf: 'flex-end',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '24px',
              alignSelf: 'flex-end'
            }}
          >
            <ButtonBase disableRipple>
              <ContentCopyOutlinedIcon
                sx={{ width: '20px' }}
                onClick={() => {
                  void copyToClipboard(textResponse, 'Response copied to clipboard!');
                }}
              />
            </ButtonBase>
            <RateAnswer
              conversationId={conversationId || ''}
              prompt={prompt}
              rating={msg.rating ?? 0}
              response={response}
              version={version}
              onRate={score => {
                onRate(msg.id, score);
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '12px' }}>Please double check responses</Typography>
        </Box>
      )}
    </Box>
  );
};

export default MessageFooter;
